import { useId, useState } from 'react';
import './retailIdExpandedDesign.css';
import BannerIntroSection from './BannerIntroSection';
import CannabinoidsSection from './CannabinoidsSection';
import PackageDetailsSection from './PackageDetailsSection';
import ProductHeroSection from './ProductHeroSection';
import ProductOverviewSection from './ProductOverviewSection';
import ProductShowcaseCarousel from './ProductShowcaseCarousel';
import RidTabs from './RidTabs';
import TerpenesSection from './TerpenesSection';
import LabResultsTextSection from './LabResultsTextSection';
import ProductHeroMetaSection from './ProductHeroMetaSection';
import ResourcesSection, { type ResourceLinks } from './ResourcesSection';
import { normalizePublicLandingUrl } from '../publicLandingUrl';
import { normalizeLandingPageLayout, type MarketLandingPageLayout } from '../marketLandingPageLayout';
import { DescriptionFormat } from '../DescriptionFormat';
import type { PublicCoaFiles } from '../../hooks/usePublicCoaResults';
import type { LicenseeNotice, RecallBannerVariant } from '../CustomLanding';
import { BatchTestResultsContent } from './BatchTestResultsSection';
import { resolvePublicLabTestStatus, usePublicBatchTestResults } from '../../hooks/usePublicBatchTestResults';

export type PassFailRow = {
  test: string;
  result?: string;
  status: 'passed' | 'nd';
};

export type CannabinoidRow = {
  cannabinoid: string;
  weight: string;
  perServing: string;
  perPackage: string;
  percent: string;
  outcomeText?: string;
  weightModeResultText?: string;
  emphasized?: boolean;
};

export type CannabinoidDisplayMode = 'Both' | 'Percent' | 'Weight';

export type TerpeneRow = {
  terpene: string;
  percent: string;
  emphasized?: boolean;
};

type CarouselSlide = {
  src: string;
  alt: string;
};

type OverviewRow = {
  label: string;
  value: string;
  mono?: boolean;
};

export type RetailIdExpandedDesignPageProps = {
  stagedSummary?: { producerName?: string };
  layout?: MarketLandingPageLayout | null;
  // Product details tab
  productImageSrc?: string;
  productTitle?: string;
  brandText?: string;
  chips?: string[];
  fullDescription?: string;
  overviewRows?: OverviewRow[];
  labTestStatus?: string;
  cannabinoids?: CannabinoidRow[];
  cannabinoidDisplayMode?: CannabinoidDisplayMode;
  terpenes?: TerpeneRow[];
  packageDetails?: readonly (readonly [string, string])[];
  resources?: ReadonlyArray<ResourceLinks>;
  coaFiles?: string[];
  coaResults?: PublicCoaFiles;
  batchTestResultsRequest?: { issuanceId: string; index: number; marketCode?: string };

  // About brand tab
  brandName?: string;
  brandTagline?: string;
  brandDescription?: string;
  brandSlides?: CarouselSlide[];
  brandSections?: { title: string; content: string }[];
  brandPurchaseUrl?: string;
  brandWebsiteUrl?: string;

  // Banner / regulatory
  stateWebsiteHref?: string;
  stateLogoSrc?: string;
  stateDescription?: string;
  stateDescriptionFormat?: DescriptionFormat;
  verifiedBadgeSrc?: string;
  regulatoryDisclaimer?: string;
  productBrandDisclaimer?: string;
  licenseeNotice?: LicenseeNotice;

  // Footer
  footerText?: string;

  // Recall alert
  recallMessage?: string;
  recallVariant?: RecallBannerVariant;
  recallMoreInfoHref?: string;
  publicHealthNoticeUrls?: readonly string[];
};

const imgChevron = '/icons/img-chevron.svg';
const imgMetrcWordmark = '/icons/img-metrc-wordmark.svg';
const imgExternalLink = '/icons/img-external-link.svg';
const imgReadMore = '/icons/img-read-more.svg';
const imgViewCoaLeft = '/icons/img-view-coa-left.svg';
const imgViewCoaRight = '/icons/img-view-coa-right.svg';
const imgPassed = '/icons/img-passed.svg';

export default function RetailIdExpandedDesignPage({
  stagedSummary,
  productImageSrc,
  productTitle = '',
  brandText = '',
  chips = [],
  fullDescription = '',
  overviewRows = [],
  labTestStatus,
  cannabinoids = [],
  cannabinoidDisplayMode = 'Both',
  terpenes = [],
  packageDetails = [],
  resources = [],
  coaFiles = [],
  coaResults,
  batchTestResultsRequest,
  brandName,
  brandTagline,
  brandDescription,
  brandSlides,
  brandSections = [],
  brandPurchaseUrl,
  brandWebsiteUrl,
  stateWebsiteHref,
  stateLogoSrc,
  stateDescription,
  stateDescriptionFormat = DescriptionFormat.PlainText,
  verifiedBadgeSrc,
  regulatoryDisclaimer,
  productBrandDisclaimer,
  licenseeNotice,
  footerText,
  recallMessage,
  recallVariant,
  recallMoreInfoHref,
  layout,
  publicHealthNoticeUrls,
}: RetailIdExpandedDesignPageProps) {
  const normalizedLayout = layout ? normalizeLandingPageLayout(layout) : null;
  const configuredSections = normalizedLayout
    ? normalizedLayout.mode === 'Regulatory' ? normalizedLayout.regulatory : normalizedLayout.standard
    : null;
  const showLivePotencySection = !stagedSummary && Boolean(batchTestResultsRequest)
    && Boolean(configuredSections?.some(section => section.id === 'cannabinoids' && !section.hidden));
  const showLiveBatchResults = !stagedSummary && Boolean(batchTestResultsRequest)
    && (!configuredSections || configuredSections.some(section => section.id === 'batch-test-results' && !section.hidden));
  const batchResults = usePublicBatchTestResults(
    showLiveBatchResults || showLivePotencySection ? batchTestResultsRequest?.issuanceId || '' : '', batchTestResultsRequest?.index ?? 0, batchTestResultsRequest?.marketCode,
  );
  const hasLivePotency = batchResults.isLoading || Boolean(batchResults.error) || !batchResults.data
    || batchResults.data.availability === 'unavailable' || batchResults.data.categories.some(category => category.kind === 'potency');
  const publicLabTestStatus = showLiveBatchResults ? resolvePublicLabTestStatus(batchResults, labTestStatus) : labTestStatus;
  const safeBrandPurchaseUrl = normalizePublicLandingUrl(brandPurchaseUrl);
  const safeBrandWebsiteUrl = normalizePublicLandingUrl(brandWebsiteUrl);
  const safeBrandSlides = brandSlides
    ?.map((slide) => ({ ...slide, src: normalizePublicLandingUrl(slide.src) }))
    .filter((slide): slide is CarouselSlide => slide.src !== null);
  const hasBrandTab = Boolean(brandName || brandDescription);
  const hasBrandLinks = Boolean(safeBrandPurchaseUrl || safeBrandWebsiteUrl);
  const topLevelTabs = [
    { id: 'product-details', label: 'Product Details' },
    ...(hasBrandTab ? [{ id: 'about-brand', label: `About ${brandName || 'Brand'}` }] : []),
  ];
  const [activeTab, setActiveTab] = useState<'about-brand' | 'product-details'>('product-details');
  const urgentRecallTitleId = useId();
  const tabsIdBase = useId();

  const isUrgentRecall = Boolean(recallMessage) && recallVariant === 'urgent';
  const isColoradoRecall = Boolean(recallMessage) && recallVariant === 'colorado';
  const noticeUrls = [...new Set((publicHealthNoticeUrls ?? [])
    .map(normalizePublicLandingUrl)
    .filter((url): url is string => url !== null))];
  const recallLinkHref = recallMoreInfoHref || stateWebsiteHref;
  const licenseeNoticeContent = licenseeNotice ? (
    <p className="rid-expanded__licensee-notice" data-testid="landing-licensee-notice">
      <strong><em>{licenseeNotice.heading}</em></strong>{' '}{licenseeNotice.message}
    </p>
  ) : null;

  const urgentRecallBanner = isUrgentRecall ? (
    <div className="rid-recall-banner" role="alert" aria-labelledby={urgentRecallTitleId}>
      <span className="rid-recall-banner__icon" aria-hidden="true">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 1 L23 12 L12 23 L1 12 Z" fill="#e11932" />
          <rect x="10.5" y="6" width="3" height="8" rx="1.5" fill="#ffffff" />
          <circle cx="12" cy="17" r="1.6" fill="#ffffff" />
        </svg>
      </span>
      <div className="rid-recall-banner__body">
        <div id={urgentRecallTitleId} className="rid-recall-banner__title">URGENT SAFETY RECALL</div>
        <div className="rid-recall-banner__text">
          {recallMessage}
          {recallLinkHref && (
            <>
              {' '}Please{' '}
              <a
                className="rid-recall-banner__link"
                href={recallLinkHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                click here
                <img
                  className="rid-recall-banner__link-icon"
                  src={imgExternalLink}
                  alt=""
                  aria-hidden="true"
                />
              </a>{' '}
              for more information.
            </>
          )}
        </div>
      </div>
    </div>
  ) : null;

  const coloradoRecallBanner = isColoradoRecall ? (
        <div className="rid-recall-banner" role="alert">
          <span className="rid-recall-banner__icon" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 1 L23 12 L12 23 L1 12 Z" fill="#e11932" />
              <rect x="10.5" y="6" width="3" height="8" rx="1.5" fill="#ffffff" />
              <circle cx="12" cy="17" r="1.6" fill="#ffffff" />
            </svg>
          </span>
          <div className="rid-recall-banner__body">
            <div className="rid-recall-banner__text">
              {recallMessage}
              {noticeUrls.map((url, index) => (
                <a
                  key={url}
                  className="rid-recall-banner__link rid-recall-banner__link--notice"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Public health notice{noticeUrls.length > 1 ? ` ${index + 1}` : ''} (opens in a new tab)
                </a>
              ))}
            </div>
          </div>
        </div>
  ) : null;

  // Pre-creation policy takes precedence over every configured section, including live result requests.
  if (stagedSummary) {
    return (
      <div className="rid-expanded" data-testid="landing-staged-package">
        {recallMessage && (coloradoRecallBanner || urgentRecallBanner || (
          <div className="rid-recall-banner rid-recall-banner--classic" role="alert">{recallMessage}</div>
        ))}
        {(stateLogoSrc || stateDescription || stateWebsiteHref) && (
          <BannerIntroSection officeLogoSrc={stateLogoSrc || ''} metrcWordmarkSrc={imgMetrcWordmark}
            externalLinkIconSrc={imgExternalLink} stateWebsiteHref={stateWebsiteHref || ''}
            description={stateDescription} descriptionFormat={stateDescriptionFormat} />
        )}
        <main className="rid-expanded__content rid-expanded__staged">
          {productTitle && <h1>{productTitle}</h1>}
          {stagedSummary.producerName && (
            <dl><dt>Produced by</dt><dd>{stagedSummary.producerName}</dd></dl>
          )}
          <p role="status">Test results will appear once this package is finalized.</p>
        </main>
        <footer className="rid-expanded__footer"><p>{footerText || `© ${new Date().getFullYear()} Metrc LLC · Powered by Retail ID`}</p></footer>
      </div>
    );
  }

  if (layout) {
    const config = normalizedLayout!;
    const sections = config.mode === 'Regulatory' ? config.regulatory : config.standard;
    const complianceCard = (
      <BannerIntroSection showHeader={layout.mode === 'Regulatory'} officeLogoSrc={stateLogoSrc || ''} metrcWordmarkSrc={imgMetrcWordmark}
        externalLinkIconSrc={imgExternalLink} stateWebsiteHref={stateWebsiteHref || ''}
        description={stateDescription} descriptionFormat={stateDescriptionFormat}
        verifiedBadgeSrc={recallMessage ? undefined : verifiedBadgeSrc} />
    );
    const content = {
      'product-image': productImageSrc ? <section className="rid-expanded__image-card"><img src={productImageSrc} alt={`${productTitle} packaging`} /></section> : null,
      'product-name': productTitle || brandText ? <ProductHeroMetaSection readMoreIconSrc={imgReadMore} productTitle={productTitle} brandText={brandText} chips={chips} fullDescription="" /> : null,
      'product-overview': <ProductOverviewSection viewCoaLeftSrc={imgViewCoaLeft} viewCoaRightSrc={imgViewCoaRight} passedIconSrc={imgPassed} rows={overviewRows} showBatchResults={false} />,
      'batch-test-results': batchTestResultsRequest
        ? <>
          {coaResults && <ProductOverviewSection viewCoaLeftSrc={imgViewCoaLeft} viewCoaRightSrc={imgViewCoaRight} passedIconSrc={imgPassed} coaResults={coaResults} showOverview={false} showTable={false} />}
          <BatchTestResultsContent {...batchTestResultsRequest} chevronSrc={imgChevron} labTestStatus={publicLabTestStatus} results={batchResults} kindFilter={showLivePotencySection ? 'nonPotency' : undefined} />
        </>
        : coaResults || labTestStatus?.trim() || coaFiles.some((file) => normalizePublicLandingUrl(file))
        ? <ProductOverviewSection viewCoaLeftSrc={imgViewCoaLeft} viewCoaRightSrc={imgViewCoaRight} passedIconSrc={imgPassed} labTestStatus={labTestStatus} coaFiles={coaFiles} coaResults={coaResults} showOverview={false} /> : null,
      'package-details': packageDetails.length ? <PackageDetailsSection chevronSrc={imgChevron} details={packageDetails} /> : null,
      cannabinoids: batchTestResultsRequest
        ? hasLivePotency ? <BatchTestResultsContent {...batchTestResultsRequest} chevronSrc={imgChevron} results={batchResults} kindFilter="potency" /> : null
        : cannabinoids.length ? <CannabinoidsSection chevronSrc={imgChevron} rows={cannabinoids} displayMode={cannabinoidDisplayMode} /> : null,
      terpenes: terpenes.length ? <TerpenesSection chevronSrc={imgChevron} rows={terpenes} /> : null,
      resources: resources.length ? <ResourcesSection chevronSrc={imgChevron} resources={resources} externalLinkIconSrc={imgExternalLink} /> : null,
      'product-details-about-brand': (
        <section>
          <RidTabs idBase={tabsIdBase} className="rid-expanded__tabs" ariaLabel="Product tabs" tabs={topLevelTabs} activeTabId={activeTab} onTabChange={(tabId) => setActiveTab(tabId as 'about-brand' | 'product-details')} />
          {licenseeNoticeContent}
          <div id={`${tabsIdBase}-panel-product-details`} role="tabpanel" aria-labelledby={`${tabsIdBase}-tab-product-details`} hidden={activeTab !== 'product-details'}>
            <ProductHeroMetaSection readMoreIconSrc={imgReadMore} productTitle="Product Details" brandText="" fullDescription={fullDescription} />
          </div>
          {hasBrandTab && <div id={`${tabsIdBase}-panel-about-brand`} role="tabpanel" aria-labelledby={`${tabsIdBase}-tab-about-brand`} hidden={activeTab !== 'about-brand'}>
            {safeBrandSlides?.length ? <ProductShowcaseCarousel slides={safeBrandSlides} className="rid-product-showcase-carousel--brand" /> : null}
            <ProductHeroMetaSection readMoreIconSrc={imgReadMore} productTitle={brandName ? `About ${brandName}` : 'About Brand'} brandText={brandTagline || ''} fullDescription={brandDescription || ''}
              purchaseUrl={safeBrandPurchaseUrl || undefined} websiteUrl={safeBrandWebsiteUrl || undefined} brandName={brandName} />
            {brandSections.map((section) => <LabResultsTextSection key={section.title} chevronSrc={imgChevron} title={section.title}><p>{section.content}</p></LabResultsTextSection>)}
          </div>}
        </section>
      ),
      footer: <footer className="rid-expanded__footer"><p>{footerText || `© ${new Date().getFullYear()} Metrc LLC · Powered by Retail ID`}</p></footer>,
    };
    return (
      <div className="rid-expanded" data-page-type={config.mode}>
        {recallMessage && (coloradoRecallBanner || urgentRecallBanner || <div className="rid-recall-banner rid-recall-banner--classic" role="alert"><span className="rid-recall-banner__text">{recallMessage}</span></div>)}
        {complianceCard}
        {!sections.some((section) => section.id === 'product-details-about-brand' && !section.hidden) && licenseeNoticeContent}
        <main className="rid-expanded__content">
          {productBrandDisclaimer && <p className="rid-expanded__disclaimer">{productBrandDisclaimer}</p>}
          {regulatoryDisclaimer && <p className="rid-regulatory-disclaimer">{regulatoryDisclaimer}</p>}
          {sections.filter((section) => !section.hidden).map((section) => <div key={section.id} data-landing-section={section.id}>{content[section.id as keyof typeof content]}</div>)}
        </main>
        {config.mode === 'Regulatory' && content.footer}
      </div>
    );
  }

  return (
    <div className="rid-expanded" data-node-id="4222:15830">
      {coloradoRecallBanner}
      <BannerIntroSection
        officeLogoSrc={stateLogoSrc || ''}
        metrcWordmarkSrc={imgMetrcWordmark}
        externalLinkIconSrc={imgExternalLink}
        stateWebsiteHref={stateWebsiteHref || ''}
        description={stateDescription}
        descriptionFormat={stateDescriptionFormat}
        verifiedBadgeSrc={recallMessage ? undefined : verifiedBadgeSrc}
        belowHeaderSlot={urgentRecallBanner}
      />

      {recallMessage && !isUrgentRecall && !isColoradoRecall && (
        <div className="rid-recall-banner rid-recall-banner--classic" role="alert">
          <span className="rid-recall-banner__icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 0L20 10L10 20L0 10L10 0Z" fill="#dc2626" />
              <text x="10" y="14" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold">!</text>
            </svg>
          </span>
          <span className="rid-recall-banner__text">{recallMessage}</span>
        </div>
      )}

      <RidTabs
        idBase={tabsIdBase}
        className="rid-expanded__tabs"
        ariaLabel="Product tabs"
        tabs={[...topLevelTabs]}
        activeTabId={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as 'about-brand' | 'product-details')}
      />

      {licenseeNoticeContent}

      <main
        id={`${tabsIdBase}-panel-product-details`}
        className="rid-expanded__content"
        role="tabpanel"
        aria-labelledby={`${tabsIdBase}-tab-product-details`}
        hidden={activeTab !== 'product-details'}
      >
        {productBrandDisclaimer && (
          <p className="rid-expanded__disclaimer">{productBrandDisclaimer}</p>
        )}
        {regulatoryDisclaimer && (
          <p className="rid-regulatory-disclaimer">{regulatoryDisclaimer}</p>
        )}
        {productImageSrc || productTitle ? (
          <ProductHeroSection
            productImageSrc={productImageSrc || ''}
            readMoreIconSrc={imgReadMore}
            productTitle={productTitle}
            brandText={brandText}
            chips={chips}
            fullDescription={fullDescription}
          />
        ) : null}
        <ProductOverviewSection
          viewCoaLeftSrc={imgViewCoaLeft}
          viewCoaRightSrc={imgViewCoaRight}
          passedIconSrc={imgPassed}
          rows={overviewRows}
          labTestStatus={publicLabTestStatus}
          coaFiles={coaFiles}
          coaResults={coaResults}
        />
        {batchTestResultsRequest && <BatchTestResultsContent {...batchTestResultsRequest} chevronSrc={imgChevron} results={batchResults} />}
        {!batchTestResultsRequest && cannabinoids.length > 0 && (
          <CannabinoidsSection
            chevronSrc={imgChevron}
            rows={cannabinoids}
            displayMode={cannabinoidDisplayMode}
          />
        )}
        {terpenes.length > 0 && <TerpenesSection chevronSrc={imgChevron} rows={terpenes} />}
        {packageDetails.length > 0 && <PackageDetailsSection chevronSrc={imgChevron} details={packageDetails} />}
        {resources.length > 0 && (
          <ResourcesSection
            chevronSrc={imgChevron}
            resources={resources}
            externalLinkIconSrc={imgExternalLink}
          />
        )}
      </main>

      {hasBrandTab && (
        <section
          id={`${tabsIdBase}-panel-about-brand`}
          role="tabpanel"
          aria-labelledby={`${tabsIdBase}-tab-about-brand`}
          hidden={activeTab !== 'about-brand'}
        >
          {safeBrandSlides && safeBrandSlides.length > 0 && <ProductShowcaseCarousel slides={safeBrandSlides} className="rid-product-showcase-carousel--brand" />}
          {hasBrandLinks && (
            <div className="rid-expanded__brand-links">
              {safeBrandPurchaseUrl && (
                <a
                  href={safeBrandPurchaseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rid-product-overview__coa-btn rid-expanded__brand-link"
                >
                  Purchase Products
                </a>
              )}
              {safeBrandWebsiteUrl && (
                <a
                  href={safeBrandWebsiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rid-product-overview__coa-btn rid-expanded__brand-link"
                >
                  {brandName ? `${brandName}'s Website` : 'Website'}
                </a>
              )}
            </div>
          )}
          <ProductHeroMetaSection
            productTitle={brandName ? `About ${brandName}` : ''}
            brandText={brandTagline || ''}
            fullDescription={brandDescription || ''}
            readMoreIconSrc={imgReadMore}
          />
          {brandSections.map((section, i) => (
            <LabResultsTextSection key={i} chevronSrc={imgChevron} title={section.title}>
              <p>{section.content}</p>
            </LabResultsTextSection>
          ))}
        </section>
      )}

      <footer className="rid-expanded__footer">
        <p>{footerText || `© ${new Date().getFullYear()} Metrc LLC · Powered by Retail ID`}</p>
      </footer>
    </div>
  );
}
